import React from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2 } from "lucide-react";
import { apiGetInvoice, apiCetakInvoice, apiRiwayatInvoice, formatApiErrorDetail } from "@/lib/api";
import { rupiah } from "@/lib/format";
import InvoiceA4 from "@/components/InvoiceA4";
import * as W from "@/lib/waktu";

// Addendum 5 (A5-C): staff invoice (Kasir/CS, Finance, Owner). Pick the
// instalment, then "Catat & cetak" records the print (append-only) and opens
// the browser's print dialog (choose "Simpan sebagai PDF" for a file).
const PILIHAN = [["", "Penuh (tanpa termin)"], ["50", "50%"], ["75", "75%"], ["100", "100%"], ["manual", "Manual…"]];
const waktu = (iso) => W.waktuSedang(iso);

export default function CetakInvoice() {
  const { id } = useParams();
  const [pilih, setPilih] = React.useState("");
  const [manual, setManual] = React.useState("");
  const [ke, setKe] = React.useState("1");
  const [d, setD] = React.useState(null);
  const [riwayat, setRiwayat] = React.useState([]);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const persen = pilih === "manual" ? manual : pilih;
  const body = () => (persen ? { persen: Number(persen), termin_ke: Number(ke) || null } : {});
  const pesan = (e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  React.useEffect(() => {
    setErr(null);
    if (pilih === "manual" && !(Number(manual) > 0)) return;
    apiGetInvoice(id, body()).then(setD).catch(pesan);
  }, [id, pilih, manual, ke]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { apiRiwayatInvoice(id).then(setRiwayat).catch(() => {}); }, [id]);

  const cetak = async () => {
    setBusy(true); setErr(null);
    try {
      const hasil = await apiCetakInvoice(id, body());
      setD(hasil);
      setRiwayat(await apiRiwayatInvoice(id));
      setTimeout(() => window.print(), 100);
    } catch (e) { pesan(e); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-2 py-4 sm:px-4 print:bg-white print:p-0">
      <div data-testid="invoice-kontrol" className="mx-auto mb-4 max-w-3xl rounded-lg bg-white p-4 text-sm text-slate-800 shadow print:hidden space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs">Termin
            <select data-testid="invoice-pilih" value={pilih} onChange={(e) => setPilih(e.target.value)} className="mt-1 rounded border border-slate-300 px-2 py-1.5 text-sm">
              {PILIHAN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          {pilih === "manual" && <label className="flex flex-col text-xs">Persen
            <input data-testid="invoice-persen" type="number" min="0.01" max="100" step="0.01" value={manual} onChange={(e) => setManual(e.target.value)} className="mt-1 w-24 rounded border border-slate-300 px-2 py-1.5 text-sm" /></label>}
          {persen && <label className="flex flex-col text-xs">Termin ke
            <input data-testid="invoice-ke" type="number" min="1" max="20" value={ke} onChange={(e) => setKe(e.target.value)} className="mt-1 w-20 rounded border border-slate-300 px-2 py-1.5 text-sm" /></label>}
          <button data-testid="invoice-cetak" onClick={cetak} disabled={busy || !d || d.dibatalkan}
            className="inline-flex items-center gap-2 rounded bg-soq-biru px-4 py-2 text-white disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Catat &amp; cetak / Simpan PDF</button>
        </div>
        <p className="text-xs text-slate-500">Tagihan termin = persen × grand total − yang sudah dibayar (tidak pernah minus). Setiap cetak dicatat.</p>
        {err && <p data-testid="invoice-error" className="text-xs text-red-600">{err}</p>}
        {riwayat.length > 0 && (
          <div data-testid="invoice-riwayat" className="text-xs">
            <div className="font-semibold">Riwayat cetak</div>
            {riwayat.map((r) => <div key={r.id}>{waktu(r.waktu)} · {r.persen ? `Termin ${r.termin_ke || ""} ${r.persen}% · tagihan ${rupiah(r.tagihan)}` : "Invoice penuh"} · {r.oleh}</div>)}
          </div>
        )}
      </div>
      {d ? <InvoiceA4 d={d} /> : !err && <div className="p-6 text-center text-slate-500">Memuat…</div>}
    </div>
  );
}
